import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Input, InputNumber, Switch, Table, Space, message, Collapse, Modal, AutoComplete } from 'antd';
import { Project, getProjectQuotaSettings, updateProjectQuotaSettings, renameProjectHospital } from '../services/project';

interface Hospital { name: string; override: number | null; limit: number | null; used: number; remaining: number | null }
interface Audit { id: number; updated_by: number; createdAt: string; before_values: any; after_values: any }
const labels: Record<string, string> = { doctor_id: '专家ID', hospital_limit_enabled: '医院限制', default_hospital_limit: '默认医院总上限', default_doctor_monthly_limit: '默认专家月上限', hospital_quotas: '医院独立额度', max_submissions: '专家累计上限', monthly_max_submissions: '专家月上限' };
const describe = (values: any) => Object.entries(values || {}).map(([k, v]) => `${labels[k] || k}：${v === null ? '未配置' : typeof v === 'boolean' ? (v ? '开启' : '关闭') : typeof v === 'object' ? JSON.stringify(v) : v}`).join('；');

labels.hospital_name = '医院名称';
labels.hospital_name_mappings = '医院名称合并关系';
const ProjectQuotaSettings: React.FC<{ projectId: number; onSaved?: () => void }> = ({ projectId, onSaved }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [hospitals, setHospitals] = useState<Hospital[]>([]);
  const [hospitalKeyword, setHospitalKeyword] = useState('');
  const [hospitalPage, setHospitalPage] = useState(1);
  const [audits, setAudits] = useState<Audit[]>([]);
  const [overrides, setOverrides] = useState<Record<string, number>>({});
  const [savedOverrides, setSavedOverrides] = useState<Record<string, number>>({});
  const [mappings, setMappings] = useState<Record<string, string>>({});
  const [dirty, setDirty] = useState(false);
  const [renameSource, setRenameSource] = useState<Hospital | null>(null);
  const [renameTarget, setRenameTarget] = useState('');
  const enabled = Form.useWatch('hospital_limit_enabled', form);
  const defaultLimit = Form.useWatch('default_hospital_limit', form);
  const filteredHospitals = hospitals.filter(hospital => hospital.name.toLocaleLowerCase().includes(hospitalKeyword.trim().toLocaleLowerCase()));
  const applyData = (data: any) => {
    form.setFieldsValue(data); setHospitals(data.hospitals); setAudits(data.audits);
    setOverrides(data.hospital_quotas || {}); setSavedOverrides(data.hospital_quotas || {});
    setMappings(data.hospital_name_mappings || {}); setDirty(false);
  };
  useEffect(() => {
    let active = true;
    setLoading(true); setError(''); setHospitalKeyword(''); setHospitalPage(1); setRenameSource(null); setDirty(false);
    getProjectQuotaSettings(projectId).then(res => {
      if (!active) return;
      if (!res.success) throw new Error(res.message);
      form.setFieldsValue(res.data); setHospitals(res.data.hospitals); setAudits(res.data.audits); setOverrides(res.data.hospital_quotas || {});
      setSavedOverrides(res.data.hospital_quotas || {}); setMappings(res.data.hospital_name_mappings || {});
    }).catch(e => { if (active) setError(e.response?.data?.message || e.message || '额度配置加载失败'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [projectId, form]);
  const save = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const res = await updateProjectQuotaSettings(projectId, { ...values, hospital_quotas: overrides } as Partial<Project>);
      if (!res.success) throw new Error(res.message);
      message.success('额度配置已保存'); onSaved?.();
      const refreshed = await getProjectQuotaSettings(projectId);
      if (!refreshed.success) throw new Error(refreshed.message);
      applyData(refreshed.data);
    } catch (e: any) {
      if (!e.errorFields) message.error(e.response?.data?.message || e.message || '保存失败');
    } finally { setSaving(false); }
  };
  const rename = async () => {
    if (!renameSource || saving) return;
    const target = renameTarget.trim();
    if (!target || target.length > 255 || target === renameSource.name) return;
    setSaving(true);
    try {
      const res = await renameProjectHospital(projectId, renameSource.name, target, mappings, savedOverrides);
      if (!res.success) throw new Error(res.message);
      message.success(res.message); setRenameSource(null); onSaved?.();
      const refreshed = await getProjectQuotaSettings(projectId);
      if (!refreshed.success) throw new Error(refreshed.message);
      applyData(refreshed.data); setHospitalKeyword(''); setHospitalPage(1);
    } catch (e: any) { message.error(e.response?.data?.message || e.message || '医院名称修改失败，请刷新后重试'); }
    finally { setSaving(false); }
  };
  const targetHospital = hospitals.find(h => h.name === renameTarget.trim());
  return <div style={{ marginBottom: 24 }}>
    {error && <Alert type="error" message={error} />}
    <Form form={form} layout="inline" disabled={loading || saving || !!error} onValuesChange={() => setDirty(true)} style={{ marginBottom: 16 }}>
      <Form.Item name="hospital_limit_enabled" label="开启医院额度限制" valuePropName="checked"><Switch /></Form.Item>
      <Form.Item name="default_hospital_limit" label="默认医院总上限" rules={[{ required: !!enabled, message: '开启后必须配置医院上限' }]}><InputNumber min={1} max={2147483647} precision={0} /></Form.Item>
      <Form.Item name="default_doctor_monthly_limit" label="专家默认每月上限" rules={[{ required: true, message: '请配置本项目专家默认月上限' }]}><InputNumber min={1} max={2147483647} precision={0} /></Form.Item>
      <Button type="primary" onClick={save} loading={saving} disabled={loading || !!error}>保存项目及医院额度</Button>
    </Form>
    <Alert showIcon type="info" style={{ marginBottom: 12 }} message="同一项目按医院名称共用总额度，不按月重置。医院独立额度留空使用项目默认值。关闭医院限制时保留配置及历史计数。提交即占用，拒绝不释放，修改原记录不重复计数。" />
    <Space wrap style={{ marginBottom: 12 }}>
      <Input aria-label="搜索医院名称" placeholder="输入医院名称或关键词搜索" allowClear value={hospitalKeyword}
        disabled={loading || saving || !!error} style={{ width: 300 }}
        onChange={event => { setHospitalKeyword(event.target.value); setHospitalPage(1); }} />
      <span>共 {filteredHospitals.length} 家医院{hospitalKeyword.trim() ? `（全部 ${hospitals.length} 家）` : ''}</span>
    </Space>
    {dirty && <div style={{ marginBottom: 12, color: '#faad14' }}>请先保存项目及医院额度，再修改医院名称。</div>}
    <Table<Hospital> rowKey="name" size="small" loading={loading} dataSource={filteredHospitals}
      locale={{ emptyText: hospitalKeyword.trim() ? '未找到匹配的医院，请调整关键词' : '暂无医院数据' }}
      pagination={{ current: hospitalPage, pageSize: 10, showSizeChanger: false, onChange: setHospitalPage }} columns={[
      { title: '医院名称', dataIndex: 'name', render: (name, r) => <Space>{name}<Button type="link" size="small" disabled={loading || saving || !!error || dirty}
        onClick={() => { setRenameSource(r); setRenameTarget(r.name); }}>修改名称</Button></Space> },
      { title: '独立总上限', render: (_, r) => <InputNumber min={1} max={2147483647} precision={0} disabled={loading || saving || !!error} value={overrides[r.name] ?? null} placeholder="使用项目默认" onChange={v => { setDirty(true); setOverrides(prev => {
        const next = { ...prev }; if (v === null) delete next[r.name]; else next[r.name] = Number(v); return next;
      }); }} /> },
      { title: '生效上限', render: (_, r) => enabled ? (overrides[r.name] || defaultLimit || '未配置') : '医院限制关闭' },
      { title: '累计已提交', dataIndex: 'used' },
      { title: '剩余', render: (_, r) => enabled && (overrides[r.name] || defaultLimit) ? Math.max(0, (overrides[r.name] || defaultLimit) - r.used) : '-' }
    ]} />
    <Modal title="修改医院配额名称" open={!!renameSource} onOk={rename} onCancel={() => { if (!saving) setRenameSource(null); }}
      confirmLoading={saving} okText={targetHospital && targetHospital.name !== renameSource?.name ? '确认合并，保留目标额度' : '保存名称'}
      cancelText="取消" closable={!saving} maskClosable={!saving} cancelButtonProps={{ disabled: saving }}
      okButtonProps={{ disabled: !renameTarget.trim() || renameTarget.trim() === renameSource?.name }}>
      <div style={{ marginBottom: 12 }}>原名称：{renameSource?.name}</div>
      <AutoComplete style={{ width: '100%', marginBottom: 16 }} value={renameTarget} disabled={saving}
        options={hospitals.filter(h => h.name !== renameSource?.name).map(h => ({ value: h.name }))}
        filterOption={(input, option) => String(option?.value || '').includes(input)} onChange={setRenameTarget}>
        <Input aria-label="新的医院配额名称" maxLength={255} placeholder="输入新名称或选择已有医院" />
      </AutoComplete>
      <Alert showIcon type="warning" message={targetHospital && targetHospital.name !== renameSource?.name ? '将合并到已有医院，保留目标医院额度' : '将修改本项目的医院配额名称'}
        description={<div>
          <div>仅修改当前项目的医院配额名称和统计归属，医生基础资料及其他项目不变。历史及后续提交按新名称汇总。</div>
          {targetHospital && targetHospital.name !== renameSource?.name ? <div>合并后上限：{targetHospital.limit ?? '未配置'}；累计已提交：{targetHospital.used + (renameSource?.used || 0)} 次。原医院额度不相加，合并次数达到上限后无法继续提交。</div>
            : <div>新名称沿用原医院额度。</div>}
        </div>} />
    </Modal>
    <Collapse items={[{ key: 'audit', label: '最近50条额度调整记录', children: <Space direction="vertical" style={{ width: '100%' }}>{audits.map(a => <div key={a.id} style={{ borderBottom: '1px solid #eee', paddingBottom: 8 }}>
      <div>{new Date(a.createdAt).toLocaleString('zh-CN')} · 操作人ID {a.updated_by}</div>
      <div>调整前：{describe(a.before_values)}</div><div>调整后：{describe(a.after_values)}</div>
    </div>)}</Space> }]} />
  </div>;
};
export default ProjectQuotaSettings;
